import { Badge, Tooltip, BadgeProps as MantineBadgeProps } from '@mantine/core'

import { cn } from 'src/utils'

interface BadgeProps extends MantineBadgeProps {
  tooltipText?: string
  color?: 'gray' | 'orange' | 'blue' | 'green' | 'red'
  textTransform?: 'uppercase' | 'none' | 'capitalize' | 'lowercase'
  clickable?: boolean
  /**
   * Renders a compact badge with 10px text. 'xs' is the tightest (used in the
   * applicant sidebar); 's' is slightly roomier (used in the Documents tab).
   */
  compact?: 'xs' | 's'
  /**
   * Classes for the outer wrapper, which is the element that takes part in a
   * parent flex/grid layout (e.g. `shrink-0`, `min-w-*`). `className` goes to
   * the Mantine Badge itself.
   */
  wrapperClassName?: string
}

// Same 10px font in both; only the height/padding footprint differs.
const compactClasses: Record<'xs' | 's', string> = {
  xs: '!h-[16px] !min-h-0 !gap-0.5 !px-1 !py-0 !text-[10px] !leading-none',
  s: '!h-[20px] !min-h-0 !gap-1 !px-2 !py-0 !text-[10px] !leading-none',
}

//TODO: replace color option with variants -- understand variant options better
const StyledBadge = ({
  tooltipText = '',
  textTransform = 'none',
  children,
  color = 'gray',
  clickable = false,
  compact,
  wrapperClassName,
  ...props
}: BadgeProps) => {
  const addStrokeColor = () => {
    switch (color) {
      case 'orange':
        return 'bg-doubleNickel-warning-50 text-doubleNickel-warning-500 stroke-doubleNickel-warning-400'
      case 'blue':
        return 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500 stroke-doubleNickel-brand-400'
      case 'green':
        return 'bg-doubleNickel-success-100 text-doubleNickel-success-600 stroke-doubleNickel-success-600'
      case 'red':
        return 'bg-doubleNickel-error-50 text-doubleNickel-error-500 stroke-doubleNickel-error-700'
      default:
        return 'bg-doubleNickel-gray-100 text-doubleNickel-gray-500 stroke-doubleNickel-gray-500'
    }
  }

  return (
    <div className={wrapperClassName}>
      <Tooltip
        label={tooltipText}
        withArrow
        disabled={!tooltipText}
        className="text-xs"
        // Mantine leaves focus off by default; with it on, a badge that is
        // given tabIndex can reveal its tooltip from the keyboard too. Touch
        // stays off so a tap on a clickable badge opens only its menu.
        events={{ hover: true, focus: true, touch: false }}
      >
        <Badge
          variant="light"
          className={cn(
            'rounded border fill-none text-xs font-medium',
            clickable && 'hover:cursor-pointer hover:shadow',
            compact && compactClasses[compact],
            textTransform,
            addStrokeColor()
          )}
          {...props}
        >
          {children}
        </Badge>
      </Tooltip>
    </div>
  )
}

export default StyledBadge
